import { useContext, useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  InnerWrapper,
  MainContainer,
  TitleAndActionBox,
} from "../../../common";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useWalletContext } from "../../../../context/WalletContext";
import { EMIONBOARDINGSTATUS, VERIFIED } from "../../../../constants";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { HeadingText, ParagraphText, UBButton } from "../../../../Libraries";
import { WalletNonidealModal } from "../../../common/Modals";
import {
  BackIconNew,
  LogoRupinNew,
  RupinFavIcon,
} from "../../../../assets/icons/icon";
import styles from "./WalletOnboardingA.module.css";

const FEATURES = [
  "free_and_instant",
  "cashback_on_easyload",
  "auto_reconciliation",
  "send_anywhere",
  "receive_anywhere",
];

const ROTATE_INTERVAL_MS = 2200;

const WalletOnboardingA = () => {
  const [I18n] = useContext(LanguageContext);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [sliding, setSliding] = useState(false);
  const navigate = useNavigate();
  const { accountInfo = {} } = useWalletContext();

  const isPending =
    accountInfo.emi_onboarding_status === EMIONBOARDINGSTATUS.SUBMITTED ||
    accountInfo.emi_onboarding_status === EMIONBOARDINGSTATUS.INPROGRESS;
  const isVerificationFailed =
    accountInfo.emi_onboarding_status === EMIONBOARDINGSTATUS.RESUBMIT;

  useEffect(() => {
    const id = setInterval(() => setSliding(true), ROTATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Once the slide transition finishes, commit the shift and snap back
  // (the recomputed list renders identically, so nothing visibly jumps).
  const handleSlideEnd = (e) => {
    if (e.propertyName !== "transform") return;
    setStep((s) => s + 1);
    setSliding(false);
  };

  const featureAt = (offset) => FEATURES[(offset + step) % FEATURES.length];

  // 5 rows: one hidden below the viewport that slides in from the bottom.
  const STRIP_OFFSETS = [0, 1, 2, 3, 4];
  const activeStripIndex = sliding ? 2 : 1;

  const handleUpgradeAccount = () => {
    triggerLogEvent(analytics, "w_kyc");
    setOpen(true);
  };

  return (
    <MainContainer className="walletOnboardingA" fluid>
      <Grid container>
        <Grid item md={12}>
          <InnerWrapper className="innerWrapper newInnerWrapper">
            <Box className={styles.content}>
              <Box className={styles.backLinkBox}>
                <Box className={styles.logoBox}>
                  <LogoRupinNew />
                </Box>
                <ParagraphText
                  className={styles.backLink}
                  id="wallet_onboarding_a_back"
                  onClick={() => {
                    const lastVisited = localStorage.getItem("lastVisited");
                    if (lastVisited?.includes("UCash")) {
                      if (
                        accountInfo.emi_onboarding_status !==
                        EMIONBOARDINGSTATUS.VERIFIED
                      ) {
                        navigate("/BusinessOverview");
                      } else {
                        navigate("/UCash");
                      }
                    } else if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate("/BusinessOverview");
                    }
                  }}
                >
                  <BackIconNew width={14} height={14} /> {I18n.back}
                </ParagraphText>
              </Box>
              <Box className={styles.contentInner}>
                <Box className={styles.featureList}>
                  <Box
                    className={`${styles.featureListInner} ${
                      sliding ? styles.featureListInnerSliding : ""
                    }`}
                    onTransitionEnd={handleSlideEnd}
                  >
                    {STRIP_OFFSETS.map((offset, i) => {
                      // While sliding, the top row fades out and the incoming
                      // bottom row fades in, so never more than 4 are visible.
                      const isHidden = sliding ? i === 0 : i === 4;
                      return (
                        // Keyed by the item's position in the infinite strip, so
                        // the same DOM node carries its text across the snap-back.
                        <HeadingText
                          key={offset + step}
                          className={`${styles.featureItem} ${
                            i === activeStripIndex
                              ? styles.featureItemActive
                              : ""
                          } ${isHidden ? styles.featureItemHidden : ""}`}
                        >
                          {I18n[featureAt(offset)]}
                        </HeadingText>
                      );
                    })}
                  </Box>
                </Box>

                <Box className={styles.card}>
                  <Box className={styles.cardIcon}>
                    <RupinFavIcon width={30} height={30} />
                  </Box>
                  <HeadingText className={styles.cardTitle}>
                    {I18n.your_account_just_got_an_upgrade}
                  </HeadingText>
                  <ParagraphText className={`fontSize12 ${styles.cardMinute}`}>
                    {I18n.it_only_takes_a_minute}
                  </ParagraphText>
                  <ParagraphText
                    className={`fontSize12 ${styles.cardSubtitle}`}
                  >
                    {I18n.unlock_benefits_by_upgrading_your_account}
                  </ParagraphText>
                  <ParagraphText
                    className={`fontSize12 ${styles.cardLicensed}`}
                  >
                    {I18n.licensed_by_sbp_in_principle}
                  </ParagraphText>
                  <UBButton
                    id="wallet_onboarding_a_upgrade_btn"
                    title={I18n.upgrade_account}
                    className={`ubPrimaryButton textUppercase ${styles.upgradeBtn}`}
                    onClick={handleUpgradeAccount}
                  />
                </Box>
              </Box>
            </Box>
          </InnerWrapper>
        </Grid>
      </Grid>
      <WalletNonidealModal
        isPending={isPending}
        setOpen={setOpen}
        open={open}
        applicationID={accountInfo.application_id || null}
        raastDetail={accountInfo.raast_info || null}
        isVerificationFailed={isVerificationFailed}
      />
    </MainContainer>
  );
};

export default WalletOnboardingA;
